import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast, ToastContainer } from "react-toastify";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { ExecuteSopsConfig } from "./ExecuteSopsConfig";
import { useTranslation } from "react-i18next";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
  getDataById,
} from "../../../../Services/Services";

import {
  getTaskConfigsURL,
  createTaskURL,
  findTaskById,
  getTaskListURL,
  updateTaskURL,
  incidentList,
  sopList,
  SopTaskTableData,
  sopResourceList,
  sopGetIncident,
  SopUserData,
  CreateSopInstance,
  getAllIncidentsByCtegorySop,
  getAllSopByIncident,
  getResourcesBasedOnRegion,
} from "../../../../Services/EndPoints";

import Loader from "react-spinner-loader";
import { downloadFile } from "../../../../Components/UiComponents/utils";
import { PaperClipIcon } from "../../../../Components/IconComponents";
import { useKeycloak } from "@react-keycloak/web";
import ExecuteSopsExpandableTable from "./ExecuteSopsExpandableTable";

const ExecuteSopsForm = () => {
  const { t } = useTranslation();
  const [sopTableData, setSopTableData] = useState([]);
  const [userTaskData, setUserTaskData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isFieldsDisabled, setIsFieldsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sopFormConfig, setSopFormConfig] = useState(ExecuteSopsConfig);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [taskConfigs, setTaskConfigs] = useState([]);
  const [viewTaskDetails, setViewTaskDetails] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [sopResources, setSopResources] = useState([]);
  const [overallPriority, setOverallPriority] = useState("Low");
  const [hasSelectedTasks, setHasSelectedTasks] = useState(false);
  const [Description, setDescription] = useState(false);
  const [incidentList, setIncidentList] = useState([]); // Store incident list
  const [regionBasedResources, setRegionBasedResources] = useState([]); // Store region-based resources
  

  const { state } = useLocation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  const taskId = state?.data?.id;
  const mediaFilePath = state?.data?.media_file_path;

  useEffect(() => {
    setDescription(true);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(),
    mode: "all",
  });

  // Watch for changes in dropdown values
  const selectedSopId = watch("task_name");
  const selectedIncidentId = watch("incident_type_id");
  const incidentcatgeoryId = watch("indident_category");

  console.log(incidentcatgeoryId, "incidentcategory");

  useEffect(() => {
    loadInitialData();
    fetchSopUserData();
  }, []);

  // Add a useEffect to watch for category changes
  useEffect(() => {
    // Check if the incident category is empty (Select Category option)
    if (!incidentcatgeoryId) {
      // Clear incident types dropdown
      updateFormConfig(0, 1, []);
      // Clear SOP dropdown
      updateFormConfig(0, 2, []);
      // Clear resource dropdown
      updateFormConfig(0, 4, []);
      // Clear selected values
      setValue("incident_type_id", "");
      setValue("task_name", "");
      setValue("sop_resource", "");
      // Clear the task list and resources
      setUserTaskData([]);
      setSopResources([]);
      setRegionBasedResources([]);
      // Clear description field
      setValue("description", "");
      // Clear assign_to_id field 
      setValue("assign_to_id", "");
      // Reset available roles
      setAvailableRoles([]);
      return;
    }

    // If a valid category is selected, fetch the related data
    fetchIncidentTypes();
    fetchSopList();
    fetchViewData();
  }, [incidentcatgeoryId]);

  // Watch for incident type changes to fetch region-based resources
  useEffect(() => {
    if (selectedIncidentId && incidentList.length > 0) {
      const selectedIncident = incidentList.find(
        (incident) => incident.id === parseInt(selectedIncidentId)
      );
      
      // Clear the sop_resource field when incident type changes
      setValue("sop_resource", "");

      if (selectedIncident && selectedIncident.parent_region_id) {
        fetchRegionBasedResources(selectedIncident.parent_region_id);
      } else {
        // Clear resources if no parent_region_id
        setRegionBasedResources([]);
        updateFormConfig(0, 4, []);
      }
    }
  }, [selectedIncidentId, incidentList]);
  
  useEffect(() => {
    if (selectedSopId && incidentcatgeoryId) {
      fetchTaskList();
      fetchSopResourceList();

      // Find the selected SOP
      const selectedSop = availableRoles.find(
        (sop) => sop.id === parseInt(selectedSopId)
      );

      if (selectedSop) {
        // Update the description field with the selected SOP's description
        setValue("description", selectedSop.description, {
          shouldValidate: true,
          shouldDirty: true,
        });

        const now = new Date();
        const formattedDate = now
          .toLocaleDateString("en-GB")
          .replace(/\//g, ":"); // Format as DD:MM:YYYY
        const formattedTime = now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });

        // Combine SOP name with date and time
        const nameValue = `${selectedSop.name}_${formattedDate} ${formattedTime}`;

        setValue("assign_to_id", nameValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [selectedSopId, availableRoles, incidentcatgeoryId]);

  useEffect(() => {
    if (selectedIncidentId) {
      fetchSopIncident();
    }
  }, [selectedIncidentId]);

  // Effect to update button state based on task selection
  useEffect(() => {
    const anyTaskSelected = userTaskData.some((task) => task.isSelected);
    setHasSelectedTasks(anyTaskSelected);
  }, [userTaskData]);

  const fetchRegionBasedResources = async (regionId) => {
    if (!regionId) {
      setRegionBasedResources([]);
      updateFormConfig(0, 4, []);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getData(getResourcesBasedOnRegion + regionId);
      console.log("Region-based resources:", response);
      
      setRegionBasedResources(response || []);
      
      // Format resources for dropdown (only include resources with status: true)
      const formattedResources = response && response.length > 0
        ? response
            .filter((resource) => resource.status === true && !resource.is_deleted)
            .map((resource) => ({
              label: `${resource.name} (${resource.agency_name})`,
              value: resource.id,
            }))
        : [];
      
      updateFormConfig(0, 4, formattedResources);
      
    } catch (error) {
      console.error("Error fetching region-based resources:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      setRegionBasedResources([]);
      updateFormConfig(0, 4, []);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTaskList = async (sopId = selectedSopId) => {
    // Only proceed if we have both incident category and SOP selected
    if (!incidentcatgeoryId || !sopId) {
      setUserTaskData([]);
      setRowCount(0);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await getDataById(SopTaskTableData, sopId);
      const sortedTasks = response
        ? response.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
        : [];
  
      // Add isSelected property to each task
      const tasksWithSelection = sortedTasks.map((task) => ({
        ...task,
        isSelected: false,
        priority: task.task_status_code || "Low", // Default priority if not provided
      }));
  
      setUserTaskData(tasksWithSelection);
      setRowCount(tasksWithSelection.length);
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchSopResourceList = async (sopId = selectedSopId) => {
    if (!incidentcatgeoryId || !sopId) {
      setSopResources([]);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await getDataById(sopResourceList, sopId);
      console.log("SOP Resource Data:", response);
      setSopResources(response || []);
    } catch (error) {
      console.error("Error fetching SOP resources:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSopIncident = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(sopGetIncident, selectedIncidentId);
      console.log("SOP Incident Data:", response);
    } catch (error) {
      console.error("Error fetching SOP incident:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSopUserData = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(SopUserData, selectedSopId);
      console.log("SOP User Data:", response);
    } catch (error) {
      console.error("Error fetching SOP user data:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialData = async () => {
    setIsLoading(true);
    await fetchTaskConfigs();
    if (taskId) {
      populateFormForEdit(taskId);
    }
    setIsLoading(false);
  };

  const fetchTaskConfigs = async () => {
    try {
      const res = await postDataWithBody(
        getTaskConfigsURL,
        ["INCDNT_CAT"],
        {}
      );
  
      setTaskConfigs(res); // Store raw config data
  
      // Process incident categories with hardcoded "Select Category" option
      const incidentCategories = [
        { label: "Select Category", value: "" }, // Added hardcoded first value
        ...res
          .filter(config => config.category_name === "INCDNT_CAT")
          .map(config => ({
            label: config.value,
            value: config.id,
          }))
      ];
  
      // Update the form config with incident categories
      const updatedConfig = [...sopFormConfig];
      if (updatedConfig[0] && updatedConfig[0].fields && updatedConfig[0].fields[0]) {
        updatedConfig[0].fields[0].options = incidentCategories;
        setSopFormConfig(updatedConfig);
      }
  
    } catch (error) {
      console.error("Failed to fetch task configs:", error);
    }
  };

  const fetchIncidentTypes = async () => {
    if (!incidentcatgeoryId) {
      updateFormConfig(0, 1, []);
      setIncidentList([]);
      return;
    }
  
    try {
      const incidentTypes = await getData(getAllIncidentsByCtegorySop + incidentcatgeoryId);
      console.log("Incident Types Response:", incidentTypes);
      
      // Store the full incident list for later use
      setIncidentList(incidentTypes || []);
      
      const formattedTypes =
        incidentTypes.length > 0
          ? incidentTypes.map((type) => ({
              label: type.name,
              value: type.id,
            }))
          : [{ label: "No options available", value: "" }];
  
      updateFormConfig(0, 1, formattedTypes);
  
      if (incidentTypes.length > 0 && !getValues("incident_type_id")) {
        const defaultIncidentId = formattedTypes[0].value.toString();
        setValue("incident_type_id", defaultIncidentId);
      }
    } catch (error) {
      console.error("Failed to fetch incident types:", error);
      updateFormConfig(0, 1, [{ label: "No options available", value: "" }]);
      setIncidentList([]);
    }
  };
  

  const fetchSopList = async () => {
    if (!incidentcatgeoryId) {
      updateFormConfig(0, 2, []);
      setAvailableRoles([]);
      setValue("assign_to_id", "");
      return;
    }
  
    try {
      const sops = await getData(getAllSopByIncident + incidentcatgeoryId);
      setAvailableRoles(sops);
  
      const formattedSops =
        sops.length > 0
          ? sops.map((sop) => ({
              label: sop.name,
              value: sop.id,
            }))
          : [{ label: "No options available", value: "" }];
  
      updateFormConfig(0, 2, formattedSops);
  
      if (sops.length > 0) {
        const defaultSopId = sops[0].id.toString();
        setValue("task_name", defaultSopId);
  
        setValue("description", sops[0].description, {
          shouldValidate: true,
          shouldDirty: true,
        });
  
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, ":");
        const formattedTime = now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
  
        const nameValue = `${sops[0].name}_${formattedDate} ${formattedTime}`;
        setValue("assign_to_id", nameValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
  
        fetchTaskList(defaultSopId);
        fetchSopResourceList(defaultSopId);
      } else {
        setValue("assign_to_id", "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } catch (error) {
      console.error("Failed to fetch SOP list:", error);
      updateFormConfig(0, 2, [{ label: "No options available", value: "" }]);
      setAvailableRoles([]);
      setValue("assign_to_id", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };
  
  
  const updateFormConfig = (configIndex, fieldIndex, options) => {
    const updatedConfig = [...sopFormConfig];
    if (updatedConfig[configIndex] && updatedConfig[configIndex].fields && updatedConfig[configIndex].fields[fieldIndex]) {
      updatedConfig[configIndex].fields[fieldIndex].options = options;
      setSopFormConfig(updatedConfig);
    }
  };

  const fetchViewData = async () => {
    const viewData = await getData(getTaskListURL);
    if (taskId) {
      populateFormForEdit(taskId);
    }
  };

  const populateFormForEdit = async (id) => {
    const response = await postDataWithBody(findTaskById, [id], {});
    const taskData = response[0];
    setViewTaskDetails(taskData);
    reset(taskData);
  };

  const generateFormFields = (fields, register, errors, control) => {
    return (
      <div>
        <div
          style={{
            fontSize: "12pt",
            marginLeft: "1rem",
            marginTop: "1rem",
            marginBottom: "1rem",
          }}
        >
          Basic Information
        </div>
        <FormHandler
          register={register}
          errors={errors}
          fields={fields}
          control={control}
          editId={taskId}
          columns={2}
          getValues={getValues}
          fileObjs={attachedFiles}
          setFileObjs={setAttachedFiles}
          Description={Description}
        />
      </div>
    );
  };

  const generateTabsContent = (tabData) => {
    tabData?.forEach((tab) => {
      tab.component = generateFormFields(
        tab?.fields,
        register,
        errors,
        control
      );
    });
    return tabData;
  };

  const handleFormSubmit = async (data, e) => {
    setIsLoading(true);
    setIsSubmitting(true);
    e.preventDefault();

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === "task_media_file" && value?.length > 0) {
        Array.from(value).forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, value);
      }
    });

    formData.append("status", true);
    if (taskId) formData.append("id", taskId);

    try {
      const headers = { "Content-Type": "multipart/form-data" };
      const response = taskId
        ? await putDataWithBody(updateTaskURL, formData, headers)
        : await postDataWithBody(createTaskURL, formData, headers);

      if (response.status === "success") {
        toast.success(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/planning/view-task");
      } else {
        toast.error(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(t("submission_error"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Convert string priority to numeric value
  const getPriorityNumericValue = (priority) => {
    switch (priority) {
      case "Low":
        return 0;
      case "Medium":
        return 1;
      case "High":
        return 2;
      default:
        return 0; // Default to Low
    }
  };

  const handleTaskSelection = (rowId, isSelected) => {
    setUserTaskData((prevData) =>
      prevData.map((row) => {
        if (row.id === rowId) {
          return { ...row, isSelected };
        }
        return row;
      })
    );
  };
  
  const handlePriorityChange = (rowId, priority) => {
    // Convert string priority to numeric value
    const priorityValue = getPriorityNumericValue(priority);

    setUserTaskData((prevData) =>
      prevData.map((row) => {
        if (row.id === rowId) {
          return { ...row, priority, priorityValue };
        }
        return row;
      })
    );
  };

  const ExecuteSop = async () => {
    setIsLoading(true);
    setIsSubmitting(true);
  
    try {
      // Get form data
      const formData = getValues();
      
      // Debug: Log form data to see what's being captured
      console.log("=== DEBUG: Form Data ===");
      console.log("All form data:", formData);
      console.log("sop_resource field:", formData.sop_resource);
      console.log("sop_resource type:", typeof formData.sop_resource);
      
      // Debug: Log resource states
      console.log("=== DEBUG: Resource States ===");
      console.log("sopResources:", sopResources);
      console.log("regionBasedResources:", regionBasedResources);
  
      // Get selected tasks
      const selectedTasksList = userTaskData.filter((task) => task.isSelected);
  
      if (selectedTasksList.length === 0) {
        toast.warning(t("Please select at least one task to execute"), {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
        setIsSubmitting(false);
        setIsLoading(false);
        return;
      }
  
      // Create taskWrapperList
      const taskWrapperList = selectedTasksList.map((task) => ({
        id: task.id.toString(),
        name: task.name,
        type: task.task_type === 0 ? "Activity" : "Notification",
        priority: getPriorityNumericValue(task.priority),
        incidentType: task.incidenttype,
        assignedTo: task.assignedto,
        role: task.role || 0,
        taskId: task.id,
        status: "TASK_EXE_STATUS_NEW_TASK",
      }));
  
      // Create resourceWrapperList - ONLY with selected resources
      let resourceWrapperList = [];
      
      console.log("=== DEBUG: Building Resource List ===");
      
      // REMOVED: Adding all SOP resources automatically
      // Only add selected region-based resources from the dropdown
      if (formData.sop_resource) {
        console.log("Processing selected region-based resources...");
        console.log("Selected resources data:", formData.sop_resource);
        
        let selectedResourceObjects = [];
        
        // Handle different data types for sop_resource
        if (Array.isArray(formData.sop_resource)) {
          selectedResourceObjects = formData.sop_resource;
        } else {
          // If it's a single object, wrap it in an array
          selectedResourceObjects = [formData.sop_resource];
        }
  
        // Extract resource IDs and create resource wrapper objects
        const selectedRegionResources = selectedResourceObjects
          .map((resourceObj) => {
            // Handle both object format {label, value} and direct ID format
            const resourceId = resourceObj.value || resourceObj;
            const resourceName = resourceObj.label || `Resource ${resourceId}`;
            
            console.log(`Processing resource ID ${resourceId} with name: ${resourceName}`);
            
            // Find the full resource details from regionBasedResources if needed
            const fullResource = regionBasedResources.find(r => r.id === parseInt(resourceId));
            
            return {
              id: resourceId.toString(),
              name: fullResource ? fullResource.name : resourceName.split(' - ')[0], // Extract name from label if needed
              used: 0,
              totalUsed: 0,
              assign: 0,
              status: "RESOURCE_EXE_STATUS_NOTUSED",
              type: "REGION_RESOURCE",
              // Additional fields from your resource structure if fullResource exists
              ...(fullResource && {
                resourceCategoryId: fullResource.rsrc_cat_id,
                facilityId: fullResource.facility_id,
                agencyId: fullResource.agency_id,
                totalQuantity: fullResource.total_quantity,
                availableQuantity: fullResource.available_quantity,
              })
            };
          })
          .filter(Boolean); // Remove null entries
  
        resourceWrapperList = [...resourceWrapperList, ...selectedRegionResources];
        console.log("Added selected region-based resources:", selectedRegionResources);
      }
  
      console.log("=== DEBUG: Final Resource List ===");
      console.log("Final resourceWrapperList:", resourceWrapperList);
      console.log("Total resources:", resourceWrapperList.length);
  
      // Create payload
      const payload = {
        incidentId: parseInt(selectedIncidentId),
        sopId: parseInt(selectedSopId),
        name: formData.assign_to_id,
        description: formData.description,
        userId: keycloak.tokenParsed?.email || "dmsadmin@bihar.gov.in",
        status: "TASK_EXE_STATUS_NEW_TASK",
        executeStatus: true,
        taskWrapperList,
        resourceWrapperList,
      };
  
      console.log("=== DEBUG: Final Payload ===");
      console.log("Execute SOP Payload:", JSON.stringify(payload, null, 2));
  
      // Send API request
      const response = await postDataWithBody(CreateSopInstance, payload, {});
  
      if (response && response.status === "success") {
        toast.success(
          `${response.msg} (SOP ID: ${response.id})` || t("Sop incident created successfully"),
          {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          }
        );
        navigate("../Sop-instance-list");
      } else {
        toast.error(response?.msg || t("Failed to execute SOP"), {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Execute SOP error:", error);
      toast.error(t("execution_error") || "Failed to execute SOP", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const handleExecuteSop = () => {
    ExecuteSop();
  };

  const getDropdownColor = (priority) => {
    switch (priority) {
      case "Low":
        return "#0166FF"; // Blue for low priority
      case "Medium":
        return "#ffaa22"; // Yellow for medium priority
      case "High":
        return "#e95060"; // Red for high priority
      default:
        return "#13845d"; // Default color
    }
  };

  const tableColumns = React.useMemo(
    () => [
      {
        Header: t("executesopstableheaders.name"),
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.tasktype"),
        accessor: "type",
        disableSortBy: true,
        // Cell: ({ value }) => (value === 0 ? "Activity" : "Notification"),
      },
      {
        Header: t("executesopstableheaders.incidenttype"),
        accessor: "incidenttype",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.assignedto"),
        accessor: "assignedto",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.priority"),
        accessor: "task_status_code",
        disableSortBy: true,
        Cell: ({ row }) => {
          const rowId = row.original.id;
          const priorityValue = row.original.priority || "Low";

          const handleSelectChange = (e) => {
            const newPriority = e.target.value;
            handlePriorityChange(rowId, newPriority);
          };

          return (
            <div>
              <select
                value={priorityValue}
                onChange={handleSelectChange}
                style={{
                  padding: "5px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: getDropdownColor(priorityValue),
                  color: "white",
                  width: "120px",
                  textAlign: "center",
                  textAlignLast: "center",
                }}
              >
                <option value="Low" style={{ textAlign: "center" }}>
                  {t("Low")}
                </option>
                <option value="Medium" style={{ textAlign: "center" }}>
                  {t("Medium")}
                </option>
                <option value="High" style={{ textAlign: "center" }}>
                  {t("High")}
                </option>
              </select>
            </div>
          );
        },
      },
      {
        Header: t("executesopstableheaders.selecttask"),
        accessor: "isSelected",
        disableSortBy: true,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.original.isSelected || false}
            onChange={(e) =>
              handleTaskSelection(row.original.id, e.target.checked)
            }
          />
        ),
      },
    ],
    [t, userTaskData]
  );

  const downloadAttachmentURL = `${process.env.REACT_APP_APP_SERVER_ADDRESS}/${process.env.REACT_APP_CORE_CONTEXT_PATH_NAME}/download/download_attachments/?directoryPath=${mediaFilePath}&fileName=Tasks.zip`;

  const tabsContent = generateTabsContent(sopFormConfig);

  return (
    <>
      <>
        <div className="mt-4">
          <Loader
            show={isLoading}
            type="body"
            stack="vertical"
            message="Loading Data"
          />

          <div className="form-body">
            <div className="registration__form--body">
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                name="CreateTaskRegForm"
              >
                <div>
                  {tabsContent?.map((tab) => (
                    <div key={tab.id}>
                      {tab.id === activeTabIndex && <div>{tab.component}</div>}
                    </div>
                  ))}
                </div>

                {taskId && mediaFilePath && (
                  <div className="multi_file_uploaded cursor-pointer">
                    <div
                      onClick={() =>
                        downloadFile(
                          downloadAttachmentURL,
                          `Tasks${Date.now()}.zip`
                        )
                      }
                    >
                      <PaperClipIcon className="cursor-pointer" /> Attached
                      files
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        <div>
          <Loader show={isLoading} type="body" message="Loading Data" />

          <ExecuteSopsExpandableTable
            data={userTaskData}
            columns={tableColumns}
            numberOfRows={7}
            showExport={false}
            showFilter={false}
            showReset={false}
            onExpand={false}
            rowCount={rowCount}
            headerTableHeadStyle={{ background: "#18a3a0", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #f0f0f0",
              borderRadius: "12px",
            }}
          />
        </div>
      </>
      <>
        <div
          className="d-flex"
          style={{ marginTop: "-4.5rem", marginLeft: "10pt" }}
        >
          <Button
            style={{
              fontSize: "10pt",
              paddingLeft: "2rem",
              paddingRight: "2rem",
              marginTop: "80px",
            }}
            onClick={handleExecuteSop}
            type="button"
            className="mx-2"
            variant="primary"
            disabled={isSubmitting || isLoading || !hasSelectedTasks} // Disable button if no tasks are selected
          >
            {isSubmitting ? t("Executing...") : t("Execute SOP")}
          </Button>
        </div>
      </>
      <ToastContainer />
    </>
  );
};

export default ExecuteSopsForm;